import React from 'react';
import _ from 'lodash';

import {
  TableRowDetail,
  TableSelection,
} from '@devexpress/dx-react-grid-material-ui';

import {
  SEED_COLUMN_WIDTHS,
  tableStateReducer,
  UPDATE_COLUMN_ORDER,
  UPDATE_COLUMN_VISIBILITY,
  UPDATE_COLUMN_WIDTHS,
  UPDATE_EXPANDED_ROWS,
  UPDATE_PAGE_SIZE,
  UPDATE_PAGINATION,
  // UPDATE_SEARCH,
  UPDATE_SELECTION,
  UPDATE_SORT,
} from '../tableStateReducer';
import {
  applyColumnSizePreset,
  calculateColumnWidth,
  dropControlSizedSavedWidths,
} from '../table.utils';
import { useWindowResizeEffect } from './useWindowResizeEffect';

const getInitialColumnOrder = (columnConfig: any, uiSettings: any) => {
  const { columnOrder: savedOrder = [] } = uiSettings;

  const colNames = Object.keys(columnConfig);
  const leftFixed = colNames.filter(
    (colName) => _.get(columnConfig, `${colName}.fixed`) === 'left'
  );
  const rightFixed = colNames.filter(
    (colName) => _.get(columnConfig, `${colName}.fixed`) === 'right'
  );

  if (savedOrder) {
    const savedRest = savedOrder.filter(
      (colName: any) =>
        _.get(columnConfig, `${colName}.fixed`) === undefined &&
        Object.prototype.hasOwnProperty.call(columnConfig, colName)
    );
    // Unpinned only. A pinned column is already placed by leftFixed or rightFixed, so listing it
    // here as well emitted it twice — and the earlier position won, which put a newly added
    // right-pinned column on the far left of the table for every user who had saved settings from
    // before it existed. Anyone opening that table for the first time saw it in the right place,
    // which is what made it look like a per-user glitch.
    const newColumns = colNames.filter(
      (colName) =>
        !savedOrder.includes(colName) &&
        _.get(columnConfig, `${colName}.fixed`) === undefined
    );
    // New columns go after the saved ones, not before them. Prepending put every column added
    // since a user's last visit in front of the ones identifying the row — the claim panel's
    // vendor detail columns landed left of Vendor itself. A saved order is the user's; a new
    // column is an addition to it, so it belongs at the end.
    return [...leftFixed, ...savedRest, ...newColumns, ...rightFixed];
  }

  const rest = colNames.filter(
    (colName) => _.get(columnConfig, `${colName}.fixed`) === undefined
  );
  return [...leftFixed, ...rest, ...rightFixed];
};

export const mapColumnNames = (columnConfig: any) =>
  Object.keys(columnConfig).reduce((acc, colName) => {
    if (Object.prototype.hasOwnProperty.call(columnConfig[colName], 'name')) {
      return { ...acc, [colName]: columnConfig[colName] };
    }
    return { ...acc, [colName]: { ...columnConfig[colName], name: colName } };
  }, {});

/**
 * A column config re-keyed by each column's `name`.
 *
 * `name` is the only identifier dx-grid, the saved ui settings and the column chooser share; a
 * config's own keys are just authoring labels, and for almost every column the two are spelled
 * the same. Where they are not -- `subCategory` naming `vendorSubCategories`, `companyName`
 * naming `name` -- the two spellings were compared against each other and the column was
 * dropped: `_.pick(savedColumns, Object.keys(columnConfig))` below looked up name-keyed saved
 * settings by config key and found nothing, so the column left the table, and the chooser with
 * it, from the second visit onward (the first visit has no saved settings to disagree with).
 * Keying by `name` once, here, is what keeps the three in the same namespace.
 */
export const keyColumnsByName = (columnConfig: any) =>
  _.keyBy(Object.values(mapColumnNames(columnConfig)), 'name');

export const useTableState = (
  columnConfig: any,
  { uiSettings = {}, ...rest }: any = {}
) => {
  // Everything below addresses columns by `name`; see keyColumnsByName.
  const config = React.useMemo(
    () => keyColumnsByName(columnConfig),
    [columnConfig]
  );
  // A columnOrder saved before columns were addressed by name holds authoring keys (`vendor`,
  // `subCategory`). Read as names those match nothing, so getInitialColumnOrder would treat the
  // user's existing columns as new and — now that new columns append — move them to the end of
  // their own table. Translating the saved order keeps it theirs; the next autosave writes names.
  const savedColumnOrder = React.useMemo(() => {
    const saved = uiSettings.columnOrder;
    if (!Array.isArray(saved)) {
      return saved;
    }
    const named: Record<string, any> = mapColumnNames(columnConfig);
    const namesByKey = Object.keys(named).reduce(
      (acc: Record<string, string>, key) => ({
        ...acc,
        [key]: named[key].name,
      }),
      {}
    );
    return saved.map((entry: any) => namesByKey[entry] ?? entry);
  }, [columnConfig, uiSettings.columnOrder]);

  const { columnWidths } = uiSettings;
  const savedColumnWidths = dropControlSizedSavedWidths(
    Array.isArray(columnWidths) ? columnWidths : [],
    Object.values(config) as { name: string; width?: unknown }[]
  );
  const defaultColumnWidths = useWindowResizeEffect(
    (tableWidth: any) =>
      Object.values(config)
        .map(applyColumnSizePreset)
        .map((column) => ({
          columnName: column.name,
          width: calculateColumnWidth(column, tableWidth),
        })),

    { delay: 500, id: rest.tableId }
  );

  // Built from the config, with only `show` taken from saved settings.
  //
  // Saved settings can name a column the code no longer defines — one removed from columnConfig
  // since the user last visited (e.g. Hostname) — and they carry no title, width or
  // getCellValue, having been through JSON. Iterating the config rather than the saved slice
  // drops the departed columns and keeps every column's definition authoritative; visibility is
  // the only thing a previous visit gets to decide.
  const savedColumns = _.pick(uiSettings.columns ?? {}, Object.keys(config));
  const reconciliation = _.mapValues(config, (col: any) => {
    const saved = savedColumns[col.name];
    return {
      ...col,
      show: saved != null ? saved.show !== false : col.show !== false,
    };
  });
  const initialState = React.useMemo(
    () => ({
      columns: reconciliation,
      sortingDisabledColumns: Object.values(config)
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        .filter((col) => col.sort === false)
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        .map((col) => ({ columnName: col.name, sortingEnabled: false })),
      hiddenColumnNames: Object.keys(reconciliation).filter(
        (colName) => reconciliation[colName].show === false
      ),
      columnOrder: getInitialColumnOrder(config, {
        ...uiSettings,
        columnOrder: savedColumnOrder,
      }),
      columnWidths: _.uniqBy(
        [...savedColumnWidths, ...defaultColumnWidths],
        'columnName'
      ),
      // Saved widths are written on every mount by useUiSettingsAutosave, not
      // only after a drag, so their presence says nothing about user intent —
      // this starts false and is flipped by an actual resize (see the reducer).
      columnWidthsUserAdjusted: false,
      search: '',
      fixedLeft: [
        TableSelection.COLUMN_TYPE,
        ...Object.keys(config).filter(
          (colName) => _.get(config, `${colName}.fixed`) === 'left'
        ),
      ],
      fixedRight: [
        ...Object.keys(config).filter(
          (colName) => _.get(config, `${colName}.fixed`) === 'right'
        ),
        TableRowDetail.COLUMN_TYPE,
      ],
      selection: [],
      expandedRowIds: [],
      page: 0,
      size: 25,
      pageSizes: [25, 50, 100],
      ...rest,
    }),
    // eslint-disable-next-line
    []
  );

  // instantiate reducer
  const [{ columns, ...state }, dispatch] = React.useReducer(
    tableStateReducer,
    initialState
  );

  // local state handlers
  const onSortingChange = React.useCallback(
    ([{ columnName, direction }]: any) => {
      dispatch({
        type: UPDATE_SORT,
        payload: { orderBy: columnName, order: direction, page: 0 },
      });
    },
    []
  );

  const onSearchValueChange = React.useCallback(() => {
    //
  }, []);

  const onCurrentPageChange = React.useCallback((newPage: any) => {
    dispatch({ type: UPDATE_PAGINATION, payload: newPage });
  }, []);

  const onPageSizeChange = React.useCallback((newSize: any) => {
    dispatch({ type: UPDATE_PAGE_SIZE, payload: { page: 0, size: newSize } });
  }, []);

  const onExpandedRowIdsChange = React.useCallback(
    (newExpanded: any) => {
      if (Array.isArray(newExpanded)) {
        return dispatch({ type: UPDATE_EXPANDED_ROWS, payload: newExpanded });
      }
      const payload = state.expandedRowIds.includes(newExpanded)
        ? []
        : [newExpanded];
      dispatch({ type: UPDATE_EXPANDED_ROWS, payload });
    },
    [state.expandedRowIds]
  );

  const onSelectionChange = React.useCallback((newSelection: any) => {
    dispatch({ type: UPDATE_SELECTION, payload: newSelection });
  }, []);

  const onOrderChange = React.useCallback(
    (newOrder: any) => {
      // `newOrder` belongs to TableColumnReordering, so filter a copy out of it
      // rather than mutating it with `_.pullAll`.
      const fixed = [...state.fixedLeft, ...state.fixedRight];
      const reorderEnabled = newOrder.filter(
        (colName: any) => !fixed.includes(colName)
      );
      const update = [
        ...state.fixedLeft,
        ...reorderEnabled,
        ...state.fixedRight,
      ].filter((el) => typeof el === 'string');
      dispatch({ type: UPDATE_COLUMN_ORDER, payload: update });
    },
    [state.fixedLeft, state.fixedRight]
  );

  const onHiddenColumnNamesChange = React.useCallback(
    (newHidden: any) => {
      const { show, ...columnRest } = columns[newHidden];

      // Both branches must produce a NEW array, never mutate the existing one.
      // dx-react-core's Getter memoizes its computed `tableColumns` and only
      // recomputes when a tracked getter value changes *by reference*, and
      // TableColumnVisibility / VisibleTableColumns are PureComponent / memo
      // keyed on `hiddenColumnNames`. Mutating in place (`_.pull`) fixes the
      // array's contents but leaves the grid rendering the cached column set
      // until a remount, so re-showing a hidden column did nothing until the
      // page was reloaded (FIR-20967). BaseTable's fill-width memo and seeding
      // effect are keyed on the same reference and depend on this too.
      const payload =
        show === false
          ? {
              columns: { [newHidden]: columnRest },
              hiddenColumnNames: state.hiddenColumnNames.filter(
                (colName: any) => colName !== newHidden
              ),
            }
          : {
              columns: { [newHidden]: { ...columnRest, show: false } },
              hiddenColumnNames: [...state.hiddenColumnNames, newHidden],
            };

      dispatch({ type: UPDATE_COLUMN_VISIBILITY, payload });

      // order columns so that the unhidden column comes last
      const newColumnOrder = [
        ...state.columnOrder.filter(
          (colName: any) =>
            colName !== newHidden && !state.fixedRight.includes(colName)
        ),
        newHidden,
        ...state.fixedRight,
      ].filter((el) => typeof el === 'string');

      dispatch({ type: UPDATE_COLUMN_ORDER, payload: newColumnOrder });
    },
    [columns, state]
  );

  // Stable identity: BaseTable's restore effect depends on this handler, and a
  // new function each render would re-run that effect on every render.
  const onColumnWidthsChange = React.useCallback((newColumnWidths: any) => {
    dispatch({ type: UPDATE_COLUMN_WIDTHS, payload: newColumnWidths });
  }, []);

  // Applies widths computed by the full-width fill pass. Separate from
  // onColumnWidthsChange so filling never masquerades as a user resize.
  const onSeedColumnWidths = React.useCallback((seededWidths: any) => {
    dispatch({ type: SEED_COLUMN_WIDTHS, payload: seededWidths });
  }, []);

  return {
    columns,
    state,
    handlers: {
      onSortingChange,
      onSearchValueChange,
      onCurrentPageChange,
      onPageSizeChange,
      onExpandedRowIdsChange,
      onSelectionChange,
      onHiddenColumnNamesChange,
      onOrderChange,
      onColumnWidthsChange,
      onSeedColumnWidths,
    },
  };
};
